import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { authApi, logoutAndClearTokens } from '@/api/endpoints';
import { mapUser } from '@/api/mappers';
import { getAccessToken, getRefreshToken, setAuthTokens } from '@/api/client';

interface RegisterPayload {
  email: string;
  username: string;
  password: string;
  displayName?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  login: (email: string, password: string) => Promise<User>;
  /** 1-bosqich: user yaratilmaydi, emailga kod yuboriladi. */
  register: (payload: RegisterPayload) => Promise<void>;
  /** 2-bosqich: kod to'g'ri bo'lsa user yaratiladi va sessiya ochiladi. */
  verifyEmail: (email: string, code: string) => Promise<User>;
  logout: () => Promise<void>;
  /** Sahifa yuklanganda saqlangan access token bilan sessiyani tiklaydi (/auth/me). */
  hydrate: () => Promise<void>;
}

const AUTH_STORAGE_KEY = 'sqluz-auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      hydrated: false,

      login: async (email, password) => {
        const res = await authApi.login(email, password);
        setAuthTokens(res.data.accessToken, res.data.refreshToken);
        const user = mapUser(res.data.user);
        set({ user, isAuthenticated: true });
        return user;
      },

      register: async (payload) => {
        // User hali yaratilmaydi — faqat kod yuboriladi
        await authApi.register(payload);
      },

      verifyEmail: async (email, code) => {
        const res = await authApi.verifyEmail(email, code);
        setAuthTokens(res.data.accessToken, res.data.refreshToken);
        const user = mapUser(res.data.user);
        set({ user, isAuthenticated: true });
        return user;
      },

      logout: async () => {
        await logoutAndClearTokens(getRefreshToken());
        set({ user: null, isAuthenticated: false });
      },

      hydrate: async () => {
        if (!getAccessToken()) {
          set({ hydrated: true });
          return;
        }
        try {
          const res = await authApi.me();
          set({ user: mapUser(res.data), isAuthenticated: true, hydrated: true });
        } catch {
          setAuthTokens(null, null);
          set({ user: null, isAuthenticated: false, hydrated: true });
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      // Server markup'i doim "kirmagan" holatda — hydration tugagach App tiklaydi.
      skipHydration: true,
      partialize: (s) => ({ user: s.user, isAuthenticated: s.isAuthenticated }),
    },
  ),
);
